import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { getBoutByCode, claimBoutRefSeat, claimJudgeSeat, subscribeToBout } from "../firebase/services";
import { useAuth } from "../hooks/useAuth";
import logo from "../assets/logo.png";

export default function JudgeJoin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [bout, setBout] = useState(location.state?.bout || null);
  const [claimingSeat, setClaimingSeat] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [autoJoinFailed, setAutoJoinFailed] = useState(false);
  const autoJoinedRef = useRef(false);

  const runLookup = async (rawCode) => {
    setError(""); setLoading(true);
    try {
      const found = await getBoutByCode(rawCode.trim().toUpperCase());
      if (!found) { setError("Bout not found. Check your code."); setLoading(false); return; }
      if (found.status === "confirmed") { setError("This bout has already been confirmed."); setLoading(false); return; }
      setBout(found);
    } catch (err) { console.error(err); setError("Something went wrong."); }
    setLoading(false);
  };

  const handleLookup = (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    runLookup(code);
  };

  // A QR code lands here with ?code=XXXXXX (same destination as typing the code by hand).
  useEffect(() => {
    if (bout) return;
    const qCode = new URLSearchParams(location.search).get("code");
    if (qCode) { setCode(qCode.toUpperCase()); runLookup(qCode); }
  }, []);

  // Keep assignments and claimed judge seats live while officials are choosing,
  // matching the pool role-picker behavior.
  useEffect(() => {
    if (!bout?.eventId || !bout?.id) return;
    const eventId = bout.eventId;
    const boutId = bout.id;
    return subscribeToBout(eventId, boutId, (latest) => {
      if (!latest) return;
      setBout((current) => ({
        ...latest,
        eventId,
        eventName: current?.eventName || "",
        accessType: current?.accessType,
        matchedOfficialNames: current?.matchedOfficialNames || [],
      }));
    });
  }, [bout?.eventId, bout?.id]);

  const handleJoin = async (role, name = null) => {
    if (!bout || joining) return;
    setJoining(true);
    setClaimingSeat(role === "ref" ? "ref" : name);
    try {
      if (role === "ref") {
        await claimBoutRefSeat(bout.eventId, bout.id);
        navigate(`/ref/${bout.eventId}/${bout.id}`);
      } else {
        await claimJudgeSeat(bout.eventId, bout.id, name);
        localStorage.setItem("judgeName", name);
        navigate(`/judge/${bout.eventId}/${bout.id}`, { state: { judgeName: name, bout } });
      }
    } catch (err) { console.error(err); alert("Failed to join."); }
    setJoining(false);
    setClaimingSeat("");
  };

  const activeJudges = bout?.activeJudges || [];

  // The code endpoint returns only roster names matching this signed-in user;
  // the complete official email map remains owner-only.
  const matchedOfficialNames = user ? (bout?.matchedOfficialNames || []) : [];
  const matchedJudgeName = bout
    ? (bout.judges || []).find((name) => {
        if (!matchedOfficialNames.includes(name)) return false;
        const key = name.replace(/[\.\#\$\/\[\]]/g, "_");
        const alreadySubmitted = bout.judgeSubmissions?.[key]?.complete;
        const isTaken = activeJudges.includes(name);
        return !alreadySubmitted && !isTaken;
      })
    : null;
  const canJoinAsRef = bout?.accessType === "boutOfficial" && !!bout.refName;
  const matchedRef = !!(canJoinAsRef && matchedOfficialNames.includes(bout.refName));

  useEffect(() => {
    if ((!matchedJudgeName && !matchedRef) || autoJoinedRef.current) return;
    autoJoinedRef.current = true;
    (async () => {
      try {
        if (matchedJudgeName) {
          await claimJudgeSeat(bout.eventId, bout.id, matchedJudgeName);
          localStorage.setItem("judgeName", matchedJudgeName);
          navigate(`/judge/${bout.eventId}/${bout.id}`, { state: { judgeName: matchedJudgeName, bout } });
        } else {
          await claimBoutRefSeat(bout.eventId, bout.id);
          navigate(`/ref/${bout.eventId}/${bout.id}`);
        }
      } catch (err) {
        console.error(err);
        autoJoinedRef.current = false;
        setAutoJoinFailed(true);
      }
    })();
  }, [matchedJudgeName, matchedRef, bout, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6">
      <Link to={location.pathname === "/bout/join" ? "/" : "/judge"} className="text-gray-600 text-sm mb-8 no-underline hover:text-gray-400">← Back</Link>
      <div className="text-center mb-8">
        <img src={logo} alt="" className="w-full max-w-xs mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2 mb-2">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-[10px] font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
        <h1 className="font-display text-2xl font-bold">Join Bout</h1>
        <p className="text-gray-500 text-sm mt-1">Enter the shared official code</p>
      </div>

      {!bout ? (
        <form onSubmit={handleLookup} className="w-full max-w-xs flex flex-col gap-4">
          <div>
          <label htmlFor="judge-join-code" className="text-xs font-bold text-gray-500 tracking-widest block mb-2 font-display">BOUT CODE</label>
          <input id="judge-join-code" aria-invalid={Boolean(error)} aria-describedby={error ? "judge-join-error" : undefined} value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))} placeholder="ABC123" maxLength={6}
              className="w-full p-4 bg-dark-800 border border-dark-500 rounded-xl text-white text-center font-display text-3xl font-extrabold tracking-[0.3em] outline-none focus:border-gray-400 uppercase" />
          </div>
        {error && <div id="judge-join-error" role="alert" className="text-red-400 text-sm text-center bg-red-900 bg-opacity-20 rounded-lg p-2">{error}</div>}
          <button type="submit" disabled={loading || code.length < 4}
            className="w-full p-4 bg-gradient-to-br from-green-action-light to-green-action text-white font-display text-lg font-bold rounded-xl tracking-wide disabled:opacity-40 border-none cursor-pointer">
            {loading ? "Finding bout..." : "Find Bout"}
          </button>
        </form>
      ) : (matchedJudgeName || matchedRef) && !autoJoinFailed ? (
        <div className="text-gray-500 text-sm">Signing you in as {matchedJudgeName || bout.refName}...</div>
      ) : (
        <div className="w-full max-w-xs">
          <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-5 text-center">
            <div className="text-gray-500 text-xs mb-2">{bout.eventName}</div>
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="font-display text-base font-bold" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</span>
              <span className="text-gray-600 text-xs">vs</span>
              <span className="font-display text-base font-bold" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</span>
            </div>
            <div className="text-gray-600 text-xs">
              {bout.phase === "pool" ? "Pool • 5-pt" : "Elimination • 3-pt"} • {bout.totalRounds}R
            </div>
          </div>

          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">WHO ARE YOU?</div>
          <div className="flex flex-col gap-2 mb-5">
            {canJoinAsRef && (
              <button onClick={() => handleJoin("ref")} disabled={joining}
                className="w-full p-3.5 rounded-xl text-left font-display text-base font-bold border cursor-pointer transition-all disabled:cursor-default"
            style={{ background: claimingSeat === "ref" ? "#1B5E20" : "#141414", borderColor: claimingSeat === "ref" ? "#81C784" : "#666", color: "#fff" }}>
                <div className="flex items-center justify-between">
                  <span>{bout.refName}</span>
                  <span className="text-xs text-gray-500">REF</span>
                </div>
              </button>
            )}
            {bout.judges.map((name) => {
              const key = name.replace(/[\.\#\$\/\[\]]/g, "_");
              const alreadySubmitted = bout.judgeSubmissions?.[key]?.complete;
              const hasPartialScores = bout.judgeSubmissions?.[key]?.rounds?.length > 0 && !alreadySubmitted;
              const isTaken = activeJudges.includes(name);
              const isDisabled = alreadySubmitted || isTaken;
              const canRejoin = !isTaken && !alreadySubmitted && hasPartialScores;
              return (
                <button key={name} onClick={() => !isDisabled && handleJoin("judge", name)} disabled={isDisabled || joining}
                  className="w-full p-3.5 rounded-xl text-left font-display text-base font-bold border cursor-pointer transition-all disabled:cursor-default"
                  style={{
                    background: alreadySubmitted ? "#111" : isTaken ? "#111" : claimingSeat === name ? "#388E3C" : canRejoin ? "#1a1a0e" : "#141414",
                    borderColor: alreadySubmitted ? "#222" : isTaken ? "#222" : claimingSeat === name ? "#4CAF50" : canRejoin ? "#444420" : "#333",
                    color: isDisabled ? "#555" : "#ccc",
                    opacity: isDisabled ? 0.5 : 1,
                  }}>
                  <div className="flex items-center justify-between">
                    <span>{name}</span>
                    {alreadySubmitted && <span className="text-xs text-gray-600">(scored)</span>}
                    {isTaken && !alreadySubmitted && <span className="text-xs text-yellow-600">(in bout)</span>}
                    {canRejoin && claimingSeat !== name && <span className="text-xs text-yellow-600">(rejoin)</span>}
                    {!isDisabled && !canRejoin && <span className="text-xs text-gray-500">JUDGE</span>}
                    {claimingSeat === name && <span>✓</span>}
                  </div>
                </button>
              );
            })}
          </div>

          <button onClick={() => { setBout(null); setClaimingSeat(""); setCode(""); }}
            className="w-full p-2 mt-3 bg-transparent border-none text-gray-600 text-sm cursor-pointer hover:text-gray-400">← Different code</button>
        </div>
      )}
    </div>
  );
}
